import { useCallback, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { SortingState } from "@tanstack/react-table";
import { useTypedSearchParams } from "react-router-typesafe-routes";
import { NotificationEnum } from "@graphql/generated/graphql.g";

import { TableColumnSettingsProvider } from "@providers/TableColumnSettingsProvider/TableColumnSettingsProvider";

import {
  ClientNotificationFilterForm,
  ClientNotificationFilterFormRef,
  ClientNotificationFilterFormValues,
} from "@components/Forms/ClientNotificationFilterForm/ClientNotificationFilterForm";
import { ClientNotificationsTable } from "@components/Tables";
import type { NotificationsViewMode } from "@components/Tables";
import {
  ClientNotificationsHistoryTableConfig,
  ClientNotificationsTableConfig,
  areFiltersActive,
  getDefaultFilterValues,
} from "@components/Tables/ClientNotificationsTable/ClientNotificationsTableConfig";
import { ClientNotificationsTableColumnCustomization } from "@components/Tables/ClientNotificationsTable/UI/ClientNotificationsTableColumnCustomization";
import {
  ClientNotificationTableColumns,
  DEFAULT_CLIENT_NOTIFICATION_TABLE_COLUMN_ORDER,
  DEFAULT_CLIENT_NOTIFICATION_TABLE_COLUMN_SETTINGS,
  DEFAULT_HISTORY_CLIENT_NOTIFICATION_TABLE_COLUMN_SETTINGS,
} from "@components/Tables/ClientNotificationsTable/UI/ClientNotificationsTableColumns";
import { useClientNotificationTableExport } from "@components/Tables/ClientNotificationsTable/utils/useClientNotificationTableExport";

import { Box } from "@design-system/Box/Box";
import { IconButton } from "@design-system/IconButton/IconButton";
import { SlideInPanel } from "@design-system/SlideInPanel";
import { Text } from "@design-system/Text/Text";

import { PortalHeaderActions } from "@layouts/PortalPage/PortalHeaderActions";
import { NotificationDetailsPanel } from "@components/Panels/NotificationDetailsPanel";

import { routes } from "@pages/routes";

import {
  sortingFromSearchParams,
  sortingToSearchParams,
} from "@utils/table/tableSearchParams";

type Props = {
  clientId: string;
  viewMode?: NotificationsViewMode;
  initialPageIndex?: number;
};

const notificationsRoute = routes.portal.clients.client.notifications;

const EMPTY_SORTING: SortingState = [];
const HISTORY_DEFAULT_SORTING: SortingState = [
  { id: ClientNotificationTableColumns.resolvedAt, desc: true },
];

const toFilterValues = (
  searchParams: ReturnType<
    typeof notificationsRoute.$deserializeSearchParams
  >,
): ClientNotificationFilterFormValues => ({
  notificationType: searchParams.notificationType.length
    ? (searchParams.notificationType as NotificationEnum[])
    : undefined,
  applicationNameSearch: searchParams.applicationNameSearch,
  resolvedById: searchParams.resolvedById,
  resolvedAt_Gte: searchParams.resolvedAt_Gte,
  resolvedAt_Lte: searchParams.resolvedAt_Lte,
});

export const ClientNotificationsCard = ({
  clientId,
  viewMode = "active",
  initialPageIndex = 0,
}: Props) => {
  const isHistoryView = viewMode === "history";
  const tableConfig = isHistoryView
    ? ClientNotificationsHistoryTableConfig
    : ClientNotificationsTableConfig;
  const defaultColumnSettings = isHistoryView
    ? DEFAULT_HISTORY_CLIENT_NOTIFICATION_TABLE_COLUMN_SETTINGS
    : DEFAULT_CLIENT_NOTIFICATION_TABLE_COLUMN_SETTINGS;
  const defaultSorting = isHistoryView
    ? HISTORY_DEFAULT_SORTING
    : EMPTY_SORTING;

  const [searchParams, setSearchParams] =
    useTypedSearchParams(notificationsRoute);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [isColumnSettingsOpen, setIsColumnSettingsOpen] = useState(false);
  const filterValues = useMemo(
    () => toFilterValues(searchParams),
    [searchParams],
  );
  const sorting = useMemo(
    () =>
      sortingFromSearchParams(
        searchParams.sort,
        searchParams.dir,
        defaultSorting,
      ),
    [searchParams.sort, searchParams.dir, defaultSorting],
  );

  const filterFormRef = useRef<ClientNotificationFilterFormRef>(null);

  const syncToUrl = useCallback(
    (
      values: ClientNotificationFilterFormValues,
      nextSorting: SortingState,
    ) => {
      const { sort, dir } = sortingToSearchParams(nextSorting);
      setSearchParams(
        {
          notificationType: values.notificationType ?? [],
          applicationNameSearch: values.applicationNameSearch,
          resolvedById: isHistoryView ? values.resolvedById : undefined,
          resolvedAt_Gte: isHistoryView ? values.resolvedAt_Gte : undefined,
          resolvedAt_Lte: isHistoryView ? values.resolvedAt_Lte : undefined,
          sort,
          dir,
        },
        { untypedSearchParams: true },
      );
    },
    [setSearchParams, isHistoryView],
  );

  const filtersActive = useMemo(() => {
    return areFiltersActive(filterValues, isHistoryView);
  }, [filterValues, isHistoryView]);

  const handleFilterClick = useCallback(() => {
    setIsFilterPanelOpen(true);
  }, []);

  const handleCloseFilterPanel = useCallback(() => {
    setIsFilterPanelOpen(false);
  }, []);

  const handleFiltersReset = useCallback(() => {
    syncToUrl(getDefaultFilterValues(), sorting);
  }, [syncToUrl, sorting]);

  const handleFilterChange = useCallback(
    (newValues: ClientNotificationFilterFormValues) => {
      syncToUrl(newValues, sorting);
      setIsFilterPanelOpen(false);
    },
    [syncToUrl, sorting],
  );

  const handleApplyFilters = useCallback(() => {
    if (filterFormRef.current) {
      syncToUrl(filterFormRef.current.getCurrentValues(), sorting);
      setIsFilterPanelOpen(false);
    }
  }, [syncToUrl, sorting]);

  const handleSortingChange = useCallback(
    (nextSorting: SortingState) => {
      syncToUrl(filterValues, nextSorting);
    },
    [syncToUrl, filterValues],
  );

  const handleColumnSettingsClick = useCallback(() => {
    setIsColumnSettingsOpen(true);
  }, []);

  const handleCloseColumnSettings = useCallback(() => {
    setIsColumnSettingsOpen(false);
  }, []);

  const { exportData, isExporting } = useClientNotificationTableExport({
    tableId: tableConfig.tableId,
    clientId,
    exportType: "csv",
    viewMode,
    notificationType: filterValues.notificationType,
    applicationNameSearch: filterValues.applicationNameSearch,
    resolvedById: filterValues.resolvedById,
    resolvedAt_Gte: filterValues.resolvedAt_Gte,
    resolvedAt_Lte: filterValues.resolvedAt_Lte,
  });

  const handleExportToCsv = useCallback(async () => {
    try {
      await exportData();
      toast.success("Notifications exported successfully!");
    } catch (error) {
      toast.error("Failed to export notifications");
      console.error("Export error:", error);
    }
  }, [exportData]);

  return (
    <TableColumnSettingsProvider
      tableId={tableConfig.tableId}
      defaultColumnSettings={defaultColumnSettings}
      defaultColumnOrder={DEFAULT_CLIENT_NOTIFICATION_TABLE_COLUMN_ORDER}
    >
      <PortalHeaderActions>
        <Box direction="row" gap={8} align="center">
          {filtersActive && (
            <Text size={12} color={"textSecondary"}>
              Filters are applied
            </Text>
          )}
          <IconButton
            size="sm"
            icon="faFilter"
            onClick={handleFilterClick}
            outline={"sm"}
            showBubble={filtersActive}
            tooltip="Show filters"
          />
          <IconButton
            size="sm"
            icon="faTableColumns"
            onClick={handleColumnSettingsClick}
            outline={"sm"}
            tooltip="Show/Hide columns"
          />
          <IconButton
            size="sm"
            icon={isExporting ? "faArrowsSpin" : "faFileCsv"}
            onClick={handleExportToCsv}
            outline={"sm"}
            tooltip={isExporting ? "Exporting..." : "Export table data to CSV"}
            disabled={isExporting}
          />
        </Box>
      </PortalHeaderActions>
      <ClientNotificationsTable
        initialPageIndex={initialPageIndex}
        clientId={clientId}
        viewMode={viewMode}
        sorting={sorting}
        onSortingChange={handleSortingChange}
        {...(filterValues.applicationNameSearch &&
          filterValues.applicationNameSearch.trim() && {
            applicationNameSearch: filterValues.applicationNameSearch,
          })}
        {...(filterValues.notificationType &&
          filterValues.notificationType.length > 0 && {
            notificationType: filterValues.notificationType,
          })}
        {...(isHistoryView &&
          filterValues.resolvedById && {
            resolvedById: filterValues.resolvedById,
          })}
        {...(isHistoryView &&
          filterValues.resolvedAt_Gte && {
            resolvedAt_Gte: filterValues.resolvedAt_Gte,
          })}
        {...(isHistoryView &&
          filterValues.resolvedAt_Lte && {
            resolvedAt_Lte: filterValues.resolvedAt_Lte,
          })}
      />

      <SlideInPanel
        isOpen={isFilterPanelOpen}
        onClose={handleCloseFilterPanel}
        onCancel={handleFiltersReset}
        onApply={handleApplyFilters}
        cancelButtonText={filtersActive ? "Clear filters" : undefined}
        applyButtonText="Apply filters"
        title={
          isHistoryView ? "Notification History Filters" : "Notification Filters"
        }
      >
        <ClientNotificationFilterForm
          ref={filterFormRef}
          onFilterChange={handleFilterChange}
          values={filterValues}
          errorsAPI={undefined}
          showHistoryFilters={isHistoryView}
        />
      </SlideInPanel>

      <ClientNotificationsTableColumnCustomization
        isOpen={isColumnSettingsOpen}
        onClose={handleCloseColumnSettings}
        defaultColumnSettings={defaultColumnSettings}
      />
      <NotificationDetailsPanel />
    </TableColumnSettingsProvider>
  );
};
